

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useTransition } from "react";
import styles from "./sidebar.module.css";
import { useUser } from "../../../app/providers/userContext";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  DollarSign,
  Calendar,
  CalendarCheck,
  Settings,
  Bell,
  UserPlus,
  ClipboardCheck,
  TrendingUp,
  School,
  Mail,
  Monitor,
  UserCog,
  Plus,
  ChevronDown,
  X,
  Grid,
  ListFilter,
  UsersRound,
  Clock,
  ArrowUpFromLine,
  ReceiptText,
  CreditCard,
  Wallet,
  BadgeIndianRupee,
  BookOpenText,
  Network,
  LockKeyhole,
  LoaderCircle,
} from "lucide-react";
import { PERMISSIONS } from "../../constants/permissions";
import { preloadWhenIdle, useRoutePreload } from "../../../app/routing/routePreload";
import { usePermissions } from "../../hooks/usePermissions";
import { usePlatformPermissions } from "../../hooks/usePlatformPermissions";
import { getImageFromUrl, getSchoolShortName } from "../../utils/utils";
import { ROLES, INSTITUTION_TYPES } from "../../constants/Constants";
import { isMessagingAdmin } from "../../../features/messaging/model/messaging";
import {
  canRenewSubscription,
  isSubscriptionRestricted,
  isSubscriptionSafePath
} from "../../constants/subscriptionAccess";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { user } = useUser();
  const isInstitute = user?.institutionType === INSTITUTION_TYPES[1];
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavigationPending, startNavigation] = useTransition();
  const [pendingMobileItem, setPendingMobileItem] = useState(null);
  const preloadRoute = useRoutePreload();
  const { hasPermission } = usePermissions();
  const platformPermissions = usePlatformPermissions();
  const [dropdownStates, setDropdownStates] = useState({});
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    const handlePopState = () => {
      if (isOpen && window.innerWidth < 992) {
        toggleSidebar();
      }
    };

    if (isOpen && window.innerWidth < 992) {
      window.history.pushState(null, "", `${window.location.pathname}${window.location.search}${window.location.hash}`);
      window.addEventListener("popstate", handlePopState);
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isOpen, toggleSidebar]);

  useEffect(() => {
    if (user?.schoolLogo) {
      setLogo(getImageFromUrl(user?.schoolLogo));
    }
  }, [user]);
  const handleLinkClick = () => {
    if (isOpen && window.innerWidth < 992) {
      toggleSidebar();
    }
  };

  const handleMobileNavigation = (event, item) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    handleLinkClick();
    if (item.path === `${location.pathname}${location.search}`) {
      return;
    }
    event.preventDefault();
    setPendingMobileItem(item);
    startNavigation(() => navigate(item.path));
  };

  const toggleDropdown = (item) => {
    setDropdownStates((prev) => ({
      ...prev,
      [item.key]: !(prev[item.key] ?? isActiveDropdown(item.subItems)),
    }));
  };

  useEffect(() => { setDropdownStates({}); }, [location.pathname, location.search]);

  const userRole = user?.roles?.[0]?.toLowerCase() || "student";
  const useSimpleAdminNav = ["principal", "admin", "school_admin"].includes(userRole);

  // Determine base path based on role
  // debugger
  const basePath =
    userRole === "principal" ||
      userRole === "admin" ||
      userRole === "school_admin"
      ? "/admin"
      : userRole === "teacher"
        ? "/teacher"
        : userRole === "super_admin"
            ? "/super_admin"
            : "/student";
  // Build navigation items based on role and permissions
  const getNavigationItems = () => {
    const items = [];

    // Dashboard - Always visible
    items.push({
      path: `${basePath}/dashboard`,
      icon: LayoutDashboard,
      label: "Dashboard",
    });
    // ========== TEACHER SPECIFIC MENU ITEMS ==========
    if (userRole === "teacher") {
      // My Classes - for viewing assigned classes
      if (hasPermission(PERMISSIONS.STAFF_ASSIGN_CLASSES)) {
        items.push({
          path: "/teacher/classes",
          icon: School,
          label: isInstitute ? "My Courses" : "My Classes",
        });
      }

      // My Class Students - Different from full student management
      if (hasPermission(PERMISSIONS.STUDENT_VIEW_CLASS)) {
        const myStudentsSubItems = [
          {
            path: "/teacher/students",
            label: "View Students",
            icon: Users,
          },
        ];

        if (hasPermission(PERMISSIONS.ACADEMIC_MARK_ATTENDANCE)) {
          myStudentsSubItems.push({
            path: "/teacher/students/attendance",
            label: "Mark Attendance",
            icon: ClipboardCheck,
          });
        }

        items.push({
          key: "my-students",
          icon: UsersRound,
          label: "My Students",
          dropdown: true,
          subItems: myStudentsSubItems,
        });
      }

      // Full Student Management (Admin-level) - Only if has STUDENT_VIEW_ALL
      if (
        hasPermission([
          PERMISSIONS.STUDENT_VIEW_ALL,
          PERMISSIONS.STUDENT_ADD,
          PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE,
        ])
      ) {
        const studentMgmtSubItems = [
          {
            path: "/teacher/student",
            label: "All Students",
            icon: Users,
          },
        ];

        if (hasPermission(PERMISSIONS.STUDENT_ADD)) {
          studentMgmtSubItems.push({
            path: "/teacher/student/add",
            label: "Add Student",
            icon: UserPlus,
          });
        }

        if (hasPermission(PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE)) {
          studentMgmtSubItems.push({
            path: "/teacher/student/attendance",
            label: "Attendance Records",
            icon: ClipboardCheck,
          });
        }

        items.push({
          key: "student-management",
          icon: GraduationCap,
          label: "Student Management",
          dropdown: true,
          subItems: studentMgmtSubItems,
        });
      }

      if (hasPermission(PERMISSIONS.STAFF_VIEW_ALL)) {
        const teacherMgmtSubItems = [
          {
            path: "/teacher/teacher",
            label: "All Teachers",
            icon: Users,
          },
        ];

        if (hasPermission(PERMISSIONS.STAFF_ADD)) {
          teacherMgmtSubItems.push({
            path: "/teacher/teacher/add",
            label: "Add Teacher",
            icon: UserPlus,
          });
        }

        if (hasPermission(PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE)) {
          teacherMgmtSubItems.push({
            path: "/teacher/teacher/attendance",
            label: "Attendance",
            icon: ClipboardCheck,
          });
        }

        items.push({
          key: "teacher-management",
          icon: UserCog,
          label: "Staff Management",
          dropdown: true,
          subItems: teacherMgmtSubItems,
        });
      }

      // My Attendance
      if (hasPermission(PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE)) {
        items.push({
          path: "/teacher/attendance",
          icon: ClipboardCheck,
          label: "My Attendance",
        });
      }

      // My Leaves - apply for and track leave applications
      items.push({
        path: "/teacher/leaves",
        icon: CalendarCheck,
        label: "My Leaves",
      });

      // My Schedule
      items.push({
        path: "/teacher/schedule",
        icon: Calendar,
        label: "My Schedule",
      });
      if (
        hasPermission([
          PERMISSIONS.ACADEMIC_CREATE_EXAM,
          PERMISSIONS.ACADEMIC_ENTER_GRADES,
          PERMISSIONS.ACADEMIC_VIEW_REPORTS,
        ])
      ) {
        const teacherExamSubItems = [];

        if (hasPermission(PERMISSIONS.ACADEMIC_ENTER_GRADES)) {
          teacherExamSubItems.push({
            path: `/teacher/exam`,
            label: "Enter Marks",
            icon: BookOpen,
          });
        }

        if (hasPermission(PERMISSIONS.ACADEMIC_CREATE_EXAM)) {
          teacherExamSubItems.push({
            path: `/teacher/exams`,
            label: "Exams",
            icon: ClipboardCheck,
          });
        }

        if (hasPermission(PERMISSIONS.ACADEMIC_VIEW_REPORTS)) {
          teacherExamSubItems.push({
            path: `/teacher/exam/reports`,
            label: "Results",
            icon: TrendingUp,
          });
        }

        if (teacherExamSubItems.length > 0) {
          items.push({
            key: "teacher-exams",
            icon: GraduationCap,
            label: "Exams & Results",
            dropdown: true,
            subItems: teacherExamSubItems,
          });
        }
      }
    }

    // ========== PRINCIPAL SPECIFIC MENU ITEMS ==========
    if (
      userRole === "principal" ||
      userRole === "admin" ||
      userRole === "school_admin"
    ) {
      // Students Section
      if (
        hasPermission([
          PERMISSIONS.STUDENT_VIEW_ALL,
          PERMISSIONS.STUDENT_VIEW_CLASS,
        ])
      ) {
        const studentSubItems = [
          {
            path: "/admin/student",
            label: "All Students",
            icon: Users,
          },
        ];

        if (hasPermission(PERMISSIONS.STUDENT_ADD)) {
          studentSubItems.push({
            path: "/admin/student/add",
            label: "Add Student",
            icon: UserPlus,
          });
        }

        if (hasPermission(PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE)) {
          studentSubItems.push({
            path: "/admin/student/attendance",
            label: "Attendance",
            icon: ClipboardCheck,
          });
        }

        items.push({
          key: "students",
          icon: GraduationCap,
          label: "Students",
          dropdown: true,
          subItems: studentSubItems,
        });
      }

      // Teachers Section
      if (hasPermission(PERMISSIONS.STAFF_VIEW_ALL)) {
        const teacherSubItems = [
          {
            path: "/admin/teacher",
            label: "All Teachers",
            icon: Users,
          },
        ];

        if (hasPermission(PERMISSIONS.STAFF_ADD)) {
          teacherSubItems.push({
            path: "/admin/teacher/add",
            label: "Add Teacher",
            icon: UserPlus,
          });
        }

        if (hasPermission(PERMISSIONS.ACADEMIC_VIEW_ATTENDANCE)) {
          teacherSubItems.push({
            path: "/admin/teacher/attendance",
            label: "Attendance",
            icon: ClipboardCheck,
          });
        }

        teacherSubItems.push({
          path: "/admin/teacher/org-chart",
          label: "Org Chart",
          icon: Network,
        });
        if (hasPermission(PERMISSIONS.STAFF_PAYROLL_MANAGEMENT)) {
          teacherSubItems.push({
            path: "/admin/teacher/payroll",
            label: "Payroll",
            icon: Wallet,
          });
        }
        items.push({
          key: "teachers",
          icon: UserCog,
          label: "Teachers",
          dropdown: true,
          subItems: teacherSubItems,
        });
      }
    }

    // ========== COMMON ITEMS (Based on permissions) ==========

    // Classes
    if (hasPermission(PERMISSIONS.STAFF_ASSIGN_CLASSES)) {
      items.push({
        path: `${basePath}/class`,
        icon: School,
        label: isInstitute ? "Courses" : "Classes",
      });
    }

    // Subjects
    if (hasPermission(PERMISSIONS.ACADEMIC_VIEW_GRADES)) {
      items.push({
        key: "academics",
        icon: BookOpen,
        label: "Academics",
        dropdown: true,
        subItems: [
          {
            path: `${basePath}/academics`,
            label: "Academic overview",
            icon: BookOpen,
          },
          {
            path: `${basePath}/assignment`,
            label: "Classwork & homework",
            icon: BookOpenText,
          },
        ],
      });
      items.push({
        key: "subjects",
        icon: BookOpen,
        label: "Subjects",
        dropdown: true,
        subItems: [
          {
            path: `${basePath}/subjects/management`,
            label: "Subject Dashboard",
            icon: BookOpen,
          },
          {
            path: `${basePath}/subjects/data`,
            label: "Subjects List",
            icon: ListFilter,
          },
          {
            path: `${basePath}/subjects/class-subjects`,
            label: "Class Subjects",
            icon: Grid,
          },
          {
            path: `${basePath}/subjects/elective-group-management`,
            label: "Elective Group",
            icon: Users,
          },
        ],
      });
    }

    // Fees
    if (hasPermission([PERMISSIONS.FEE_VIEW_ALL, PERMISSIONS.FEE_COLLECT, PERMISSIONS.FEE_STRUCTURE_MANAGE, PERMISSIONS.FEE_STRUCTURE_CREATE, PERMISSIONS.FEE_STRUCTURE_EDIT, PERMISSIONS.FEE_STRUCTURE_ASSIGN])) {
      const feeSubItems = [];

      // ── Fee Operations ──
      if (hasPermission(PERMISSIONS.FEE_VIEW_ALL) || hasPermission(PERMISSIONS.FEE_COLLECT)) {
        feeSubItems.push({
          path: `${basePath}/fees/student-record`,
          label: "Fee Record",
          icon: ReceiptText,
        });
      }

      if (hasPermission([
        PERMISSIONS.FEE_STRUCTURE_MANAGE,
        PERMISSIONS.FEE_STRUCTURE_CREATE,
        PERMISSIONS.FEE_STRUCTURE_EDIT,
      ])) {
        feeSubItems.push({
          path: `${basePath}/fees`,
          label: "Fee Structure",
          icon: CreditCard,
        });
      }
      if (hasPermission([
        PERMISSIONS.FEE_STRUCTURE_MANAGE,
        PERMISSIONS.FEE_STRUCTURE_CREATE,
        PERMISSIONS.FEE_STRUCTURE_EDIT,
      ])) {
        feeSubItems.push({
          path: `${basePath}/fees/management`,
          label: "Fee Management",
          icon: Wallet,
        });
      }

      // ── Fee Structure ──

      if (feeSubItems.length > 0) {
        items.push({
          key: "fees",
          icon: BadgeIndianRupee,
          label: "Fees",
          dropdown: true,
          subItems: feeSubItems,
        });
      }
    }

    // Timetable
    if (userRole === ROLES.TEACHER.toLowerCase() || userRole === ROLES.PRINCIPAL.toLowerCase() || userRole === ROLES.ADMIN.toLowerCase() || userRole === ROLES.SCHOOL_ADMIN.toLowerCase()) {
      items.push({
        path: `${basePath}/timetable`,
        icon: Calendar,
        label: "Timetable",
      });
    }

    // Events & Notice
    items.push({
      path: `${basePath}/event`,
      icon: Bell,
      label: "Events & Notice",
    });

    if (!hasPermission(PERMISSIONS.ACADEMIC_VIEW_GRADES)) {
      items.push({
        path: `${basePath}/assignment`,
        icon: BookOpen,
        label: "Homework & Projects",
      });
    }

    // School Settings
    if (hasPermission(PERMISSIONS.SYSTEM_CONFIG)) {
      items.push({
        path: `${basePath}/setup`,
        icon: Settings,
        label: "Admin Hub",
      });
    }

    // ========== SUPER ADMIN ITEMS ==========
    if (userRole === "super_admin") {
      items.push({
        key: "platform-messaging",
        icon: Bell,
        label: "SMS & WhatsApp",
        dropdown: true,
        subItems: [
          { path: "/super_admin/messaging?section=providers", icon: Settings, label: "Provider Settings" },
          { path: "/super_admin/messaging?section=mobile-login", icon: UserCog, label: "Mobile OTP Login" },
          { path: "/super_admin/messaging?section=templates", icon: Mail, label: "Message Templates" },
          { path: "/super_admin/messaging?section=balances", icon: Wallet, label: "School Balances & Grants" },
          { path: "/super_admin/messaging?section=deliveries", icon: ListFilter, label: "School Delivery History" },
          { path: "/super_admin/messaging?section=unknown", icon: ClipboardCheck, label: "Review Unknown Outcomes" },
          { path: "/super_admin/messaging?section=credential-test", icon: School, label: "School Credential Tests" },
          { path: "/super_admin/messaging-credential-test", icon: Monitor, label: "Credential Diagnostics" },
        ],
      });
    } else if (isMessagingAdmin(user)) {
      items.push({
        path: "/admin/messaging",
        icon: Bell,
        label: "Notifications & Messaging",
      });
    }

    if (userRole !== "super_admin" && isMessagingAdmin(user)) {
      items.push({
        path: `${basePath}/messaging-credential-test`,
        icon: Bell,
        label: "Messaging Credential Test",
      });
    }

    if (userRole === "super_admin") {
      items.push(
        {
          path: "/platform",
          icon: Network,
          label: "Platform console",
        },
        {
          path: "/billing",
          icon: ReceiptText,
          label: "Billing",
        },
        {
          path: "/super_admin/pricing",
          icon: BadgeIndianRupee,
          label: "Pricing",
        },
        {
          path: "/super_admin/monitor",
          icon: Monitor,
          label: "Monitor",
        },
        {
          path: "/super_admin/email-management",
          icon: Mail,
          label: "Email Management",
        },
        {
          path: "/super_admin/school-onboard",
          icon: Plus,
          label: "Onboard School",
        },
        {
          path: "/super_admin/school",
          icon: School,
          label: "Schools",
        },
        {
          path: "/super_admin/role-management",
          icon: School,
          label: "Role Management",
        },
      );
    }

    if (
      userRole !== "super_admin" &&
      ["PLATFORM_SCHOOLS_VIEW", "PLATFORM_SCHOOLS_MANAGE", "PLATFORM_LEADS_VIEW", "PLATFORM_LEADS_MANAGE", "PLATFORM_PRICING_MANAGE", "BILLING_VIEW", "BILLING_MANAGE", "BILLING_REFUND"]
        .some((permission) => platformPermissions.isPermissionGranted(permission))
    ) {
      items.push({
        path: "/platform",
        icon: Network,
        label: "Platform console",
      });
    }

    if (
      userRole !== "super_admin" &&
      [PERMISSIONS.BILLING_VIEW, PERMISSIONS.BILLING_MANAGE, PERMISSIONS.BILLING_REFUND]
        .some((permission) => user?.permission?.includes(permission))
    ) {
      items.push({
        path: "/billing",
        icon: ReceiptText,
        label: "Billing",
      });
    }

    // ========== STUDENT ITEMS ==========
    if (userRole === "student") {
      items.push(
        {
          path: "/student/attendance",
          icon: ClipboardCheck,
          label: "Attendance",
        },
        {
          path: "/student/timetable",
          icon: Calendar,
          label: "Schedule",
        },
        {
          path: "/student/fee",
          icon: DollarSign,
          label: "Fees",
        },
        {
          path: "/student/profile",
          icon: UserCog,
          label: "Profile",
        },
        {
          path: "/student/results",
          icon: BookOpen,
          label: "Results",

        },
        {
          path: "/student/progress",
          icon: TrendingUp,
          label: "Progress",
        }
      );
    }

    // Profile for non-students
    if (userRole === "teacher") {
      items.push({
        path: "/teacher/profile",
        icon: UserCog,
        label: "Profile",
      });
    }

    const utilsSubItems = [];

    if (hasPermission(PERMISSIONS.ACADEMIC_MARK_ATTENDANCE)) {
      utilsSubItems.push({
        path: `/setting/attendance/scheduler`,
        label: "Attendance Scheduler",
        icon: Clock,
      });
    }

    if (utilsSubItems.length > 0) {
      items.push({
        key: "utils",
        icon: Clock,
        label: "Utils",
        dropdown: true,
        subItems: utilsSubItems,
      });
    }
    if (
      hasPermission([PERMISSIONS.STUDENT_VIEW_ALL, PERMISSIONS.SYSTEM_CONFIG])
    ) {
      items.push({
        path: `${basePath}/promotions`,
        icon: ArrowUpFromLine,
        label: "Year-End Promotions",
      });
    }

    // Year-End Promotions
    if (
      hasPermission([PERMISSIONS.STUDENT_VIEW_ALL, PERMISSIONS.SYSTEM_CONFIG])
    ) {
      items.push({
        path: `${basePath}/enquiry`,
        icon: BookOpenText,
        label: "Enquiry Form",
      });
    }
    if (isInstitute && ["teacher", "principal", "admin", "school_admin"].includes(userRole)) {
      if (userRole !== "teacher") items.push({
        path: `${basePath}/batch`,
        icon: Clock,
        label: "Shift Management",
      });
      items.push({
        path: `${basePath}/batch/workflows`,
        icon: UsersRound,
        label: "Batch students & homework",
      });
      if (userRole === "teacher") {
        items.push({
          path: "/teacher/course-assignments",
          icon: BookOpen,
          label: "My Course Assignments",
        });
      } else {
        items.push({
          path: "/admin/course-assignments",
          icon: BookOpen,
          label: "Teacher-course Assignments",
        });
      }
    }
    if (userRole === "principal" || userRole === "school_admin" || userRole === "admin") {
      const leaveLink = {
        path: `${basePath}/leaves`,
        icon: Calendar,
        label: "Leave Management",
      };
      const teachers = items.find(item => item.key === "teachers");
      if (teachers) teachers.subItems.push(leaveLink);
      else items.push({ key: "teachers", icon: UserCog, label: "Teachers", dropdown: true, subItems: [leaveLink] });
    }

    // Exams & Results - Principal
    if (
      hasPermission([
        PERMISSIONS.ACADEMIC_CREATE_EXAM,
        PERMISSIONS.ACADEMIC_VIEW_REPORTS,
        PERMISSIONS.ACADEMIC_ENTER_GRADES,
      ])
    ) {
      const examSubItems = [];

      if (hasPermission(PERMISSIONS.ACADEMIC_CREATE_EXAM)) {
        examSubItems.push({
          path: `${basePath}/exam`,
          label: "Exams",
          icon: ClipboardCheck,
        });
        examSubItems.push({
          path: `${basePath}/exam/category`,
          label: "Exam Categories",
          icon: Grid,
        });
      }

      if (hasPermission(PERMISSIONS.ACADEMIC_ENTER_GRADES)) {
        examSubItems.push({
          path: `${basePath}/exam/marks`,
          label: "Enter Marks",
          icon: BookOpen,
        });
      }

      if (examSubItems.length > 0) {
        items.push({
          key: "exams",
          icon: GraduationCap,
          label: "Exams & Results",
          dropdown: true,
          subItems: examSubItems,
        });
      }
    }

    items.push({
      path: "/how-it-works",
      icon: BookOpenText,
      label: "How Scholaro works",
    });

    return items;
  };

  const originalItems = getNavigationItems();
  const groupedItems = useSimpleAdminNav ? (() => {
    const primary = [];
    const more = [];
    const courseItems = [];
    let attendance;
    for (const item of originalItems) {
      if (item.path === `${basePath}/dashboard` || item.key === "fees") {
        primary.push(item);
      } else if (item.key === "students") {
        const attendanceLink = item.subItems.find(link => link.path === `${basePath}/student/attendance`);
        attendance = attendanceLink;
        primary.push({ ...item, subItems: item.subItems.filter(link => link !== attendanceLink) });
      } else if (item.key === "teachers") {
        const staffLinks = item.subItems.filter(link => !["Org Chart", "Payroll"].includes(link.label));
        primary.push({ ...item, subItems: staffLinks });
        const staffTools = item.subItems.filter(link => !staffLinks.includes(link));
        if (staffTools.length) more.push({
          key: "staff-tools", label: "Staff tools", icon: UserCog,
          dropdown: true, subItems: staffTools,
        });
      } else if ([
        `${basePath}/class`, `${basePath}/batch`, `${basePath}/batch/workflows`,
        `${basePath}/course-assignments`, `${basePath}/timetable`,
      ].includes(item.path)) {
        const labels = {
          [`${basePath}/class`]: isInstitute ? "Courses" : "All Classes",
          [`${basePath}/batch`]: "Batches / Shifts",
          [`${basePath}/batch/workflows`]: "Students & homework",
          [`${basePath}/course-assignments`]: "Assign teachers",
        };
        courseItems.push({ ...item, label: labels[item.path] || item.label });
      } else {
        more.push(item);
      }
    }
    if (courseItems.length) primary.splice(2, 0, {
      key: "classes-learning", label: isInstitute ? "Courses & Batches" : "Classes", icon: BookOpen,
      dropdown: true, subItems: courseItems,
    });
    if (attendance) primary.splice(Math.min(3, primary.length), 0, { ...attendance, label: "Attendance" });
    if (more.length) primary.push({
      key: "more", label: "More", icon: Grid, dropdown: true, subItems: more,
    });
    return primary;
  })() : ["teacher", "student"].includes(userRole) ? (() => {
    const primary = [];
    const more = [];
    const primaryPaths = [
      `${basePath}/dashboard`, `${basePath}/classes`, `${basePath}/schedule`,
      `${basePath}/assignment`, `${basePath}/attendance`, `${basePath}/timetable`,
    ];
    for (const item of originalItems) {
      if (item.key === "academics") {
        const assignments = item.subItems.find(link => link.path === `${basePath}/assignment`);
        if (assignments) primary.push({ ...assignments, label: "Homework & Projects" });
        const remaining = item.subItems.filter(link => link !== assignments);
        if (remaining.length) more.push({ ...item, subItems: remaining });
      } else if (primaryPaths.includes(item.path) || item.key === "my-students") {
        primary.push(item);
      } else {
        more.push(item);
      }
    }
    if (userRole === "student" && !primary.some(item => item.path === "/student/assignment")) {
      primary.splice(1, 0, { path: "/student/assignment", label: "Homework & Projects", icon: BookOpenText });
    }
    if (more.length) primary.push({ key: "more", label: "More", icon: Grid, dropdown: true, subItems: more });
    return primary;
  })() : originalItems;
  const restricted = isSubscriptionRestricted(user);
  const navigationItems = restricted ? [
    { path: `${basePath}/dashboard`, label: "Subscription paused", icon: LockKeyhole },
    ...(canRenewSubscription(user) ? [{ path: "/admin/setup/pricing", label: "Renew subscription", icon: CreditCard }] : []),
    ...originalItems.filter(item => item.path && item.path !== `${basePath}/dashboard` && isSubscriptionSafePath(user, item.path)),
    { path: "/support", label: "Help & Support", icon: Bell },
  ] : groupedItems;

  const getAppTitle = () => {
    if (userRole === "super_admin") {
      return { full: "Admin Management", short: "AM" };
    }

    // If user has system config permissions, show as admin
    if (hasPermission(PERMISSIONS.SYSTEM_CONFIG)) {
      return {
        full: user?.schoolName || "School Management",
        short: user?.schoolName ? getSchoolShortName(user?.schoolName) : "SMS",
      };
    }

    // If user has multiple management permissions, show as management portal
    const managementPerms = [
      PERMISSIONS.STAFF_VIEW_ALL,
      PERMISSIONS.STUDENT_ADD,
      PERMISSIONS.FEE_COLLECT,
      PERMISSIONS.ACADEMIC_VIEW_GRADES,
    ];

    const managementCount = managementPerms.filter((perm) =>
      hasPermission(perm),
    ).length;

    if (managementCount >= 2 && userRole === "teacher") {
      return { full: "Management Portal", short: "MP" };
    }

    // Default based on role
    switch (userRole) {
      case "teacher":
        return { full: "Teacher Portal", short: "TP" };
      case "principal":
      case "school_admin":
      case "admin":
        return { full: "School Management", short: "SMS" };
      case "student":
      default:
        return { full: "Student Portal", short: "SP" };
    }
  };

  const appTitle = getAppTitle();
  const quickMobileItems = useSimpleAdminNav
    ? navigationItems.filter(item => item.path === `${basePath}/dashboard` || ["students", "classes-learning", "fees"].includes(item.key))
        .map(item => item.dropdown ? { ...item, path: item.subItems[0].path } : item)
        .slice(0, 4)
    : navigationItems.filter((item) => !item.dropdown && item.path).slice(0, 4);

  const quickMobilePathsKey = quickMobileItems.map((item) => item.path).join("|");
  useEffect(
    () => preloadWhenIdle(quickMobilePathsKey ? quickMobilePathsKey.split("|") : [], preloadRoute),
    [quickMobilePathsKey, preloadRoute],
  );
  const preloadOnIntent = (path) => () => preloadRoute(path);

  const getDestinationPaths = (items) => items.flatMap(item =>
    item.dropdown ? getDestinationPaths(item.subItems) : [item.path],
  );

  const activeDestinationPath = getDestinationPaths(navigationItems)
    .filter(path => location.pathname === path || location.pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  const isActiveLink = (path) => {
    if (path.includes("?")) {
      const [pathname, search] = path.split("?");
      const requestedSection = new URLSearchParams(search).get("section");
      const currentSection = new URLSearchParams(location.search).get("section");
      const activeSection = ["providers", "mobile-login", "templates", "balances", "deliveries", "unknown", "credential-test"].includes(currentSection) ? currentSection : "providers";
      return location.pathname === pathname && requestedSection === activeSection;
    }
    return path === activeDestinationPath;
  };

  const isActiveDropdown = (subItems) => {
    return subItems?.some((item) => item.dropdown
      ? isActiveDropdown(item.subItems)
      : isActiveLink(item.path));
  };

  const isActiveNavigationItem = item => item.dropdown
    ? isActiveDropdown(item.subItems)
    : isActiveLink(item.path);

  const renderNavigationItem = (item, nested = false) => {
    const active = isActiveNavigationItem(item);
    const expanded = item.dropdown && (dropdownStates[item.key] ?? active);
    const Icon = item.icon;
    return (
      <li key={item.key || item.path} className={styles.navItem}>
        {item.dropdown ? (
          <div className={styles.dropdownContainer}>
            <button
              className={`${nested ? styles.dropdownItem : styles.navLink} ${styles.dropdownToggle} ${active ? styles.active : ""}`}
              onClick={() => toggleDropdown(item)}
              type="button"
              aria-expanded={expanded}
              aria-controls={`sidebar-group-${item.key}`}
            >
              <span className={styles.linkContent}>
                <Icon className={nested ? styles.subIcon : styles.icon} size={nested ? 16 : 20} />
                <span className={nested ? styles.subLabel : styles.label}>{item.label}</span>
              </span>
              <ChevronDown
                className={`${styles.chevron} ${expanded ? styles.rotated : ""}`}
                size={16}
              />
            </button>
            <div
              id={`sidebar-group-${item.key}`}
              hidden={!expanded}
              className={`${styles.dropdownMenu} ${item.key === "more" || nested ? styles.moreMenu : ""} ${expanded ? styles.show : ""}`}
            >
              <ul className={styles.subList}>
                {item.subItems.map(subItem => renderNavigationItem(subItem, true))}
              </ul>
            </div>
          </div>
        ) : (
          <Link
            to={item.path}
            className={`${nested ? styles.dropdownItem : styles.navLink} ${active ? styles.active : ""}`}
            aria-current={active ? "page" : undefined}
            onClick={handleLinkClick}
            onPointerDown={preloadOnIntent(item.path)}
            onMouseEnter={preloadOnIntent(item.path)}
            onFocus={preloadOnIntent(item.path)}
          >
            <span className={styles.linkContent}>
              {Icon && <Icon className={nested ? styles.subIcon : styles.icon} size={nested ? 16 : 20} />}
              <span className={nested ? styles.subLabel : styles.label}>{item.label}</span>
            </span>
          </Link>
        )}
      </li>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className={styles.overlay}
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <div className={`${styles.sidebar} ${isOpen ? styles.open : ""}`}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logoContainer}>
            <div className={styles.logo}>
              
              {logo ? (
                <img
                  src={logo}
                  alt="School Logo"
                  className={styles.logoImage}
                  onError={() => setLogo(null)}
                />
              ) : (
                <School className={styles.logoIcon} size={28} />
              )}
            </div>
            <div className={styles.titleContainer}>
              <h2 className={styles.title}>
                <span className="d-none d-sm-inline">{appTitle.full}</span>
                <span className="d-sm-none">{appTitle.short}</span>
              </h2>
            </div>
          </div>
          <button
            className={`${styles.closeBtn} d-lg-none`}
            onClick={toggleSidebar}
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
        </div>

        <nav className={styles.sidebarNav} aria-label="Main navigation">
          <ul className={styles.navList}>
            {navigationItems.map(item => renderNavigationItem(item))}
          </ul>
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              <UserCog size={20} />
            </div>
            <div className={styles.userDetails}>
              <div className={styles.userName}>
                {user?.name || (user?.roles ? user.roles[0] : "User")}
              </div>
              <div className={styles.userEmail}>{user?.email}</div>
            </div>
          </div>
        </div>
      </div>

      <nav
        className={`${styles.mobileBottomNav} ${isOpen ? styles.mobileBottomNavHidden : ""}`}
        aria-label="Primary navigation"
        style={{ gridTemplateColumns: `repeat(${Math.max(quickMobileItems.length + 1, 2)}, minmax(0, 1fr))` }}
      >
        {isNavigationPending && pendingMobileItem && (
          <div className={styles.mobileNavigationStatus} role="status">
            Opening {pendingMobileItem.label}…
          </div>
        )}
        {quickMobileItems.map((item) => (
          <Link
            key={`mobile-${item.path}`}
            to={item.path}
            className={`${styles.mobileBottomNavItem} ${
              isNavigationPending
                ? pendingMobileItem?.path === item.path ? styles.mobileBottomNavItemActive : ""
                : isActiveNavigationItem(item) ? styles.mobileBottomNavItemActive : ""
            }`}
            onClick={(event) => handleMobileNavigation(event, item)}
            onPointerDown={preloadOnIntent(item.path)}
            aria-current={isActiveNavigationItem(item) ? "page" : undefined}
            aria-busy={isNavigationPending && pendingMobileItem?.path === item.path ? true : undefined}
          >
            {isNavigationPending && pendingMobileItem?.path === item.path
              ? <LoaderCircle size={18} className={styles.mobileNavigationSpinner} aria-hidden="true" />
              : <item.icon size={18} />}
            <span className={styles.mobileBottomNavLabel}>
              {item.label === "Events & Notice" ? "Notice" : item.label}
            </span>
          </Link>
        ))}
        <button
          type="button"
          className={styles.mobileBottomNavItem}
          onClick={toggleSidebar}
          aria-label="Open navigation menu"
        >
          <Grid size={18} />
          <span className={styles.mobileBottomNavLabel}>Menu</span>
        </button>
      </nav>
    </>
  );
};

export default Sidebar;
